import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Image,
  FileImage,
  FileVideo,
  File,
  Download,
  Upload,
  Search,
  Filter,
  Grid3X3,
  List,
  Eye,
  Edit,
  Copy,
  Share2,
  Tag,
  Calendar,
  HardDrive,
  Users,
  BarChart3,
  RefreshCw,
  Settings,
  ArrowLeft,
  ArrowRight,
  Folder,
  Star,
  ExternalLink,
  Activity,
  TrendingUp,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Zap,
  Layers,
  Archive
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import type { 
  SiteAsset,
  AssetFolder,
  AssetSEO,
  AssetUsage,
  AssetAnalytics,
  RepositoryImage,
  GalleryPhoto
} from '@/types/gallery';

// Asset types and categories
const assetTypes = [
  { value: 'image', label: 'Images', icon: FileImage },
  { value: 'video', label: 'Videos', icon: FileVideo },
  { value: 'document', label: 'Documents', icon: File }
];

const assetCategories = [
  { value: 'brand', label: 'Brand Assets', color: 'bg-purple-100 text-purple-800' },
  { value: 'marketing', label: 'Marketing Materials', color: 'bg-blue-100 text-blue-800' },
  { value: 'gallery', label: 'Gallery Images', color: 'bg-green-100 text-green-800' },
  { value: 'social', label: 'Social Media', color: 'bg-pink-100 text-pink-800' },
  { value: 'website', label: 'Website Assets', color: 'bg-orange-100 text-orange-800' },
  { value: 'template', label: 'Templates', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'logo', label: 'Logos & Branding', color: 'bg-yellow-100 text-yellow-800' }
];

const licenseTypes = [
  { value: 'internal', label: 'Internal Use Only' },
  { value: 'commercial', label: 'Commercial Use' },
  { value: 'editorial', label: 'Editorial Use' },
  { value: 'creative_commons', label: 'Creative Commons' },
  { value: 'royalty_free', label: 'Royalty Free' },
  { value: 'rights_managed', label: 'Rights Managed' }
];

interface AssetFilters {
  type?: string;
  category?: string;
  license?: string;
  folder_id?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  min_size?: number;
  max_size?: number;
  is_archived?: boolean;
  has_usage?: boolean;
}

interface UnifiedAsset {
  id: string;
  type: 'site_asset' | 'repository_image' | 'gallery_photo';
  filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  width?: number;
  height?: number;
  category?: string;
  license?: string;
  folder_id?: string;
  folder_name?: string;
  description?: string;
  keywords?: string[];
  usage_count: number;
  last_used?: string;
  created_at: string;
  updated_at?: string;
  seo?: AssetSEO;
  analytics?: {
    views: number;
    downloads: number;
    shares: number;
  };
}

interface AssetStats {
  total_assets: number;
  total_size: number;
  by_type: Record<string, number>;
  by_category: Record<string, number>;
  by_license: Record<string, number>;
  most_used: UnifiedAsset[];
  recent_uploads: UnifiedAsset[];
  storage_usage: {
    used: number;
    total: number;
    percentage: number;
  };
}

// Fetch unified assets from multiple sources
const fetchUnifiedAssets = async (filters: AssetFilters & { page: number; pageSize: number }) => {
  // Fetch from site_assets table
  let siteAssetsQuery = supabase
    .from('site_assets')
    .select(`
      *,
      asset_folders!inner(name),
      asset_seo(*),
      asset_usage(count)
    `);

  // Fetch from repository images
  let repositoryQuery = supabase
    .from('images')
    .select(`
      *,
      image_folders(name)
    `);

  // Fetch from gallery photos
  let galleryQuery = supabase
    .from('gallery_photos')
    .select(`
      *,
      galleries!inner(title, franchise_region)
    `);

  // Apply filters to each query
  if (filters.search) {
    const searchPattern = `%${filters.search}%`;
    siteAssetsQuery = siteAssetsQuery.ilike('filename', searchPattern);
    repositoryQuery = repositoryQuery.ilike('file_name', searchPattern);
    galleryQuery = galleryQuery.ilike('caption', searchPattern);
  }

  if (filters.type) {
    const mimePrefix = filters.type === 'image' ? 'image/' : 
                      filters.type === 'video' ? 'video/' : 
                      filters.type === 'document' ? 'application/' : '';
    if (mimePrefix) {
      siteAssetsQuery = siteAssetsQuery.like('mime_type', `${mimePrefix}%`);
      repositoryQuery = repositoryQuery.like('mime_type', `${mimePrefix}%`);
    }
  }

  if (filters.folder_id) {
    siteAssetsQuery = siteAssetsQuery.eq('folder_id', filters.folder_id);
    repositoryQuery = repositoryQuery.eq('folder_id', filters.folder_id);
  }

  if (filters.is_archived !== undefined) {
    siteAssetsQuery = siteAssetsQuery.eq('is_archived', filters.is_archived);
  }

  if (filters.date_from) {
    siteAssetsQuery = siteAssetsQuery.gte('created_at', filters.date_from);
    repositoryQuery = repositoryQuery.gte('created_at', filters.date_from);
    galleryQuery = galleryQuery.gte('created_at', filters.date_from);
  }

  if (filters.date_to) {
    siteAssetsQuery = siteAssetsQuery.lte('created_at', filters.date_to);
    repositoryQuery = repositoryQuery.lte('created_at', filters.date_to);
    galleryQuery = galleryQuery.lte('created_at', filters.date_to);
  }

  // Execute queries
  const [siteAssetsResult, repositoryResult, galleryResult] = await Promise.all([
    siteAssetsQuery,
    repositoryQuery,
    galleryQuery
  ]);

  if (siteAssetsResult.error) throw siteAssetsResult.error;
  if (repositoryResult.error) throw repositoryResult.error;
  if (galleryResult.error) throw galleryResult.error;

  // Transform and unify the data
  const unifiedAssets: UnifiedAsset[] = [];

  // Process site assets
  (siteAssetsResult.data || []).forEach(asset => {
    unifiedAssets.push({
      id: asset.id,
      type: 'site_asset',
      filename: asset.filename,
      file_path: asset.file_path,
      file_size: asset.file_size,
      mime_type: asset.mime_type,
      width: asset.width,
      height: asset.height,
      category: 'website', // Default category for site assets
      folder_id: asset.folder_id,
      folder_name: asset.asset_folders?.name,
      description: asset.asset_seo?.description,
      keywords: asset.asset_seo?.keywords,
      usage_count: asset.asset_usage?.length || 0,
      created_at: asset.created_at,
      updated_at: asset.updated_at,
      seo: asset.asset_seo,
      analytics: {
        views: 0, // Would need to be calculated from asset_analytics
        downloads: 0,
        shares: 0
      }
    });
  });

  // Process repository images
  (repositoryResult.data || []).forEach(image => {
    unifiedAssets.push({
      id: image.id,
      type: 'repository_image',
      filename: image.file_name,
      file_path: image.file_path,
      file_size: image.file_size,
      mime_type: image.mime_type,
      category: 'gallery',
      folder_id: image.folder_id,
      folder_name: image.image_folders?.name,
      description: image.description,
      usage_count: 0, // Would need to be calculated
      created_at: image.created_at,
      updated_at: image.updated_at,
      analytics: {
        views: 0,
        downloads: 0,
        shares: 0
      }
    });
  });

  // Process gallery photos
  (galleryResult.data || []).forEach((photo: any) => {
    unifiedAssets.push({
      id: photo.id.toString(),
      type: 'gallery_photo',
      filename: `gallery-${photo.id}`,
      file_path: photo.image_url,
      file_size: 0, // Not available in gallery_photos
      mime_type: 'image/jpeg', // Assumed
      category: 'gallery',
      description: photo.caption,
      usage_count: 0,
      created_at: photo.created_at,
      analytics: {
        views: 0,
        downloads: 0,
        shares: 0
      }
    });
  });

  // Sort by creation date (newest first)
  unifiedAssets.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Apply pagination
  const start = (filters.page - 1) * filters.pageSize;
  const end = start + filters.pageSize;
  const paginatedAssets = unifiedAssets.slice(start, end);

  return {
    assets: paginatedAssets,
    totalCount: unifiedAssets.length,
    page: filters.page,
    pageSize: filters.pageSize
  };
};

// Fetch asset folders
const fetchAssetFolders = async () => {
  const { data, error } = await supabase
    .from('asset_folders')
    .select('*')
    .order('name');
  
  if (error) throw error;
  return data || [];
};

// Calculate asset statistics
const calculateAssetStats = (assets: UnifiedAsset[]): AssetStats => {
  const stats: AssetStats = {
    total_assets: assets.length,
    total_size: assets.reduce((sum, asset) => sum + asset.file_size, 0),
    by_type: {},
    by_category: {},
    by_license: {},
    most_used: [],
    recent_uploads: [],
    storage_usage: {
      used: 0,
      total: 10000000000, // 10GB default limit
      percentage: 0
    }
  };

  // Calculate by type
  assets.forEach(asset => {
    const type = asset.mime_type.split('/')[0];
    stats.by_type[type] = (stats.by_type[type] || 0) + 1;
    
    if (asset.category) {
      stats.by_category[asset.category] = (stats.by_category[asset.category] || 0) + 1;
    }
    
    if (asset.license) {
      stats.by_license[asset.license] = (stats.by_license[asset.license] || 0) + 1;
    }
  });

  // Most used assets
  stats.most_used = assets
    .filter(asset => asset.usage_count > 0)
    .sort((a, b) => b.usage_count - a.usage_count)
    .slice(0, 5);

  // Recent uploads
  stats.recent_uploads = assets
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  // Storage usage
  stats.storage_usage.used = stats.total_size;
  stats.storage_usage.percentage = (stats.storage_usage.used / stats.storage_usage.total) * 100;

  return stats;
};

// Utility functions
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileTypeIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return FileImage;
  if (mimeType.startsWith('video/')) return FileVideo;
  return File;
};

const getCategoryBadge = (category?: string) => {
  const categoryInfo = assetCategories.find(c => c.value === category);
  if (!categoryInfo) return null;
  
  return (
    <Badge className={categoryInfo.color}>
      {categoryInfo.label}
    </Badge>
  );
};

export default function AssetLibrary() {
  // State management
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [selectedAsset, setSelectedAsset] = useState<UnifiedAsset | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeTab, setActiveTab] = useState<'all' | 'images' | 'videos' | 'documents'>('all');
  
  // Filters and pagination
  const [filters, setFilters] = useState<AssetFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [searchQuery, setSearchQuery] = useState('');
  
  const queryClient = useQueryClient();

  // Fetch data
  const { data: assetsData, isLoading: isAssetsLoading, refetch: refetchAssets } = useQuery({
    queryKey: ['unified-assets', { filters, currentPage, pageSize, searchQuery }],
    queryFn: () => fetchUnifiedAssets({
      ...filters,
      search: searchQuery,
      page: currentPage,
      pageSize
    })
  });

  const { data: folders = [] } = useQuery({
    queryKey: ['asset-folders'],
    queryFn: fetchAssetFolders
  });

  // Helper functions
  const handleFilterChange = (key: keyof AssetFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const toggleSelection = (assetId: string) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    const allAssetIds = filteredAssets.map(asset => asset.id);
    setSelectedAssets(new Set(allAssetIds));
  };

  const clearSelection = () => {
    setSelectedAssets(new Set());
  };

  const handleAssetView = (asset: UnifiedAsset) => {
    setSelectedAsset(asset);
    setIsDetailDialogOpen(true);
  };

  const handleDownload = async (asset: UnifiedAsset) => {
    // Create download link
    const link = document.createElement('a');
    link.href = asset.file_path;
    link.download = asset.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Track download analytics
    // This would typically update asset_analytics table
  };

  // Calculate stats and filter assets
  const assets = assetsData?.assets || [];
  const stats = useMemo(() => calculateAssetStats(assets), [assets]);

  const filteredAssets = useMemo(() => {
    let filtered = assets;

    // Filter by active tab
    if (activeTab !== 'all') {
      const typeMap = {
        images: 'image',
        videos: 'video',
        documents: 'application'
      };
      const mimePrefix = typeMap[activeTab as keyof typeof typeMap];
      filtered = filtered.filter(asset => asset.mime_type.startsWith(mimePrefix));
    }

    return filtered;
  }, [assets, activeTab]);

  const totalPages = Math.ceil((assetsData?.totalCount || 0) / pageSize);

  if (isAssetsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Asset Library</h1>
          <p className="text-gray-600">Unified media management across all platforms and sources</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetchAssets()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Upload className="w-4 h-4 mr-2" />
                Upload Assets
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload New Assets</DialogTitle>
                <DialogDescription>
                  Upload new assets to the library
                </DialogDescription>
              </DialogHeader>
              <div className="text-center py-8">
                <Upload className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">Asset upload functionality coming soon</p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Layers className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Assets</p>
                <p className="text-2xl font-bold">{stats.total_assets}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <HardDrive className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Storage Used</p>
                <p className="text-2xl font-bold">{formatFileSize(stats.total_size)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Most Used</p>
                <p className="text-2xl font-bold">{stats.most_used.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Activity className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Storage Usage</p>
                <p className="text-2xl font-bold">{stats.storage_usage.percentage.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Controls */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              
              <Select value={filters.category || ''} onValueChange={(value) => 
                handleFilterChange('category', value || undefined)
              }>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Categories</SelectItem>
                  {assetCategories.map(category => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.license || ''} onValueChange={(value) => 
                handleFilterChange('license', value || undefined)
              }>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Licenses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Licenses</SelectItem>
                  {licenseTypes.map(license => (
                    <SelectItem key={license.value} value={license.value}>
                      {license.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedAssets.size > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{selectedAssets.size} selected</Badge>
                  <Button size="sm" variant="outline" onClick={clearSelection}>
                    Clear
                  </Button>
                  <Button size="sm" variant="outline" onClick={selectAll}>
                    Select All
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="all">All ({stats.total_assets})</TabsTrigger>
          <TabsTrigger value="images">Images ({stats.by_type.image || 0})</TabsTrigger>
          <TabsTrigger value="videos">Videos ({stats.by_type.video || 0})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({stats.by_type.application || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredAssets.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Layers className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No assets found</h3>
                <p className="text-gray-600 mb-6">Upload your first assets to get started</p>
                <Button onClick={() => setIsUploadDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Assets
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className={cn(
                viewMode === 'grid' 
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  : "space-y-4"
              )}>
                {filteredAssets.map((asset) => {
                  const IconComponent = getFileTypeIcon(asset.mime_type);
                  const isSelected = selectedAssets.has(asset.id);

                  return (
                    <Card 
                      key={asset.id} 
                      className={cn(
                        "hover:shadow-md transition-shadow cursor-pointer",
                        isSelected && "ring-2 ring-blue-500"
                      )}
                      onClick={() => toggleSelection(asset.id)}
                    >
                      {viewMode === 'grid' ? (
                        <CardContent className="p-4">
                          {/* Asset preview */}
                          <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden relative group">
                            {asset.mime_type.startsWith('image/') ? (
                              <img
                                src={asset.file_path}
                                alt={asset.filename}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            <div className="fallback-icon hidden absolute inset-0 flex items-center justify-center">
                              <IconComponent className="h-12 w-12 text-gray-400" />
                            </div>
                            
                            {/* Overlay actions */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="flex space-x-2">
                                <Button 
                                  size="sm" 
                                  variant="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssetView(asset);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(asset);
                                  }}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(asset.file_path);
                                  }}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Asset type badge */}
                            <div className="absolute top-2 left-2">
                              <Badge variant="secondary" className="text-xs">
                                {asset.type.replace('_', ' ').toUpperCase()}
                              </Badge>
                            </div>
                          </div>

                          {/* Asset details */}
                          <div className="space-y-2">
                            <h3 className="font-medium text-sm truncate">{asset.filename}</h3>
                            
                            <div className="flex items-center gap-2">
                              {getCategoryBadge(asset.category)}
                              {asset.license && (
                                <Badge variant="outline" className="text-xs">
                                  {licenseTypes.find(l => l.value === asset.license)?.label}
                                </Badge>
                              )}
                            </div>

                            <div className="flex justify-between items-center text-xs text-gray-500">
                              <span>{formatFileSize(asset.file_size)}</span>
                              <span>{new Date(asset.created_at).toLocaleDateString()}</span>
                            </div>

                            {asset.usage_count > 0 && (
                              <div className="flex items-center gap-1 text-xs text-blue-600">
                                <Activity className="w-3 h-3" />
                                Used {asset.usage_count} times
                              </div>
                            )}
                          </div>
                        </CardContent>
                      ) : (
                        // List view
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              {asset.mime_type.startsWith('image/') ? (
                                <img
                                  src={asset.file_path}
                                  alt={asset.filename}
                                  className="w-16 h-16 object-cover rounded"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                                  <IconComponent className="h-8 w-8 text-gray-400" />
                                </div>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-medium truncate">{asset.filename}</h3>
                                  <Badge variant="secondary" className="text-xs">
                                    {asset.type.replace('_', ' ')}
                                  </Badge>
                                </div>
                                
                                {asset.description && (
                                  <p className="text-sm text-gray-600 truncate mb-2">
                                    {asset.description}
                                  </p>
                                )}
                                
                                <div className="flex items-center gap-2 text-xs">
                                  {getCategoryBadge(asset.category)}
                                  <span className="text-gray-500">
                                    {formatFileSize(asset.file_size)} • {new Date(asset.created_at).toLocaleDateString()}
                                  </span>
                                  {asset.usage_count > 0 && (
                                    <span className="text-blue-600">
                                      Used {asset.usage_count} times
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssetView(asset);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(asset);
                                }}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, assetsData?.totalCount || 0)} of {assetsData?.totalCount || 0} assets
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Asset Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedAsset?.filename}</DialogTitle>
            <DialogDescription>
              Asset details, usage, and analytics
            </DialogDescription>
          </DialogHeader>
          
          {selectedAsset && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Asset preview */}
              <div className="space-y-4">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  {selectedAsset.mime_type.startsWith('image/') ? (
                    <img
                      src={selectedAsset.file_path}
                      alt={selectedAsset.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {(() => {
                        const IconComponent = getFileTypeIcon(selectedAsset.mime_type);
                        return <IconComponent className="h-24 w-24 text-gray-400" />;
                      })()}
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex space-x-2">
                  <Button 
                    className="flex-1"
                    onClick={() => handleDownload(selectedAsset)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigator.clipboard.writeText(selectedAsset.file_path)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="outline">
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Asset metadata */}
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-3">Asset Information</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Type:</strong> {selectedAsset.type.replace('_', ' ')}</div>
                    <div><strong>File Size:</strong> {formatFileSize(selectedAsset.file_size)}</div>
                    <div><strong>MIME Type:</strong> {selectedAsset.mime_type}</div>
                    {selectedAsset.width && selectedAsset.height && (
                      <div><strong>Dimensions:</strong> {selectedAsset.width} × {selectedAsset.height}</div>
                    )}
                    <div><strong>Created:</strong> {new Date(selectedAsset.created_at).toLocaleString()}</div>
                    {selectedAsset.updated_at && (
                      <div><strong>Modified:</strong> {new Date(selectedAsset.updated_at).toLocaleString()}</div>
                    )}
                  </div>
                </div>

                {selectedAsset.category && (
                  <div>
                    <h4 className="font-semibold mb-3">Category & Tags</h4>
                    <div className="space-y-2">
                      {getCategoryBadge(selectedAsset.category)}
                      {selectedAsset.keywords && selectedAsset.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {selectedAsset.keywords.map(keyword => (
                            <Badge key={keyword} variant="outline" className="text-xs">
                              {keyword}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold mb-3">Usage Analytics</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Times Used:</strong> {selectedAsset.usage_count}</div>
                    <div><strong>Views:</strong> {selectedAsset.analytics?.views || 0}</div>
                    <div><strong>Downloads:</strong> {selectedAsset.analytics?.downloads || 0}</div>
                    {selectedAsset.last_used && (
                      <div><strong>Last Used:</strong> {new Date(selectedAsset.last_used).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>

                {selectedAsset.folder_name && (
                  <div>
                    <h4 className="font-semibold mb-3">Location</h4>
                    <div className="flex items-center gap-2">
                      <Folder className="w-4 h-4" />
                      <span className="text-sm">{selectedAsset.folder_name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}